import { type ApiFn, useBaseApiCall } from '@/api/factory/base/api';
import { useCookies } from '@/hooks/use-cookies';
import { useRouter } from '@/navigation';
import { LOGIN_ROUTES } from '@/routes';
import { Cookie } from '@/types/storage';
import { isAxiosError } from 'axios';
import { useSearchParams } from 'next/navigation';

export const useAuthorizedApiCall = <TResponse, TParams extends unknown[] = []>(
  callFn: ApiFn<TResponse, TParams>,
) => {
  const router = useRouter();
  const clearTokenParam = useSearchParams()?.get('clearToken');

  const JWT = useCookies(Cookie.LOGIN_SECRET);

  return useBaseApiCall<TResponse, TParams>(
    async (axiosInstance, ...params: TParams) => {
      if (!JWT) return Promise.reject();
      if (JWT) {
        axiosInstance.defaults.headers.common['Authorization'] = JWT;
      }

      try {
        return await callFn(axiosInstance, ...params);
      } catch (error) {
        if (
          isAxiosError(error) &&
          error.response?.status === 401 &&
          !clearTokenParam
        ) {
          router.replace(`${LOGIN_ROUTES.LOGIN}?clearToken=true`);

          throw {
            data: {
              data: undefined,
            },
            message: 'unauthorized',
          };
        }

        throw error;
      }
    },
  );
};
